import { ErrorModal } from "@dexteel/mesf-core";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  SkipNext,
} from "@mui/icons-material";
import { debounce } from "lodash-es";
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HeatData } from "../../models/HeatReportModels";
import { searchHeatsWithProcessId } from "../../repository/HeatReportRepository";
interface HeatProps {
  Name: string;
  Id: number;
}

interface Props {
  iconButton?: ReactElement;
  showNext?: boolean;
  showPrevious?: boolean;
  isProcessLoading?: boolean;
  value: HeatData;
  secondValue?: string;

  onButtonClick?: () => void;
  onNext?: (heatId: number | null) => void;
  onPrevious?: (heatId: number | null) => void;
  onLast?: () => void;
  onChange?: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

export const HeatNavigatorWithSearch = ({
  iconButton,
  showNext,
  showPrevious,
  value,
  isProcessLoading = false,
  onButtonClick,
  onNext,
  onPrevious,
  onLast,
  onChange,
  onBlur,
  onFocus,
}: Props) => {
  const theme = useTheme();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [heats, setHeats] = useState<HeatProps[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<HeatProps | null>({
    Name: value.HeatName,
    Id: value.HeatId!,
  });
  const [isFocused, setFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const currentSearch = useRef(searchTerm);

  currentSearch.current = searchTerm;

  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      setLoading(true);
      try {
        if (term.length >= 2) {
          const resp = await searchHeatsWithProcessId(term);
          if (resp.ok) {
            const data = resp.data.tables[0].rows;
            if (currentSearch.current === term) setHeats(data);
          } else {
            setError(resp.message);
          }
        }
        if (term.length === 0) {
          const resp = await searchHeatsWithProcessId(term);
          if (resp.ok) {
            if (currentSearch.current === term)
              setHeats(resp.data.tables[0].rows);
          } else {
            setError(resp.message);
          }
        }
      } catch (error) {
        setError("An error occurred while searching");
        setHeats([]);
      } finally {
        setLoading(false);
      }
    }, 1500),
    [],
  );
  const handleSelectionChange = (event: any, newValue: HeatProps | null) => {
    if (newValue) {
      if (newValue.Id !== value.HeatId) {
        setSelectedHeat(newValue);
        if (onChange) {
          onChange(newValue.Id);
        }

        setTimeout(() => {
          document.getElementById("dummyDiv")?.focus();
        }, 500);
      }
    } else {
      setSelectedHeat(null);
    }
  };

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  const options = useMemo(() => {
    if (selectedHeat && !heats.some((heat) => heat.Id === selectedHeat.Id)) {
      return [selectedHeat, ...heats];
    }
    return heats;
  }, [heats, selectedHeat]);

  useEffect(() => {
    if (!isFocused && !loading && !isProcessLoading) {
      if (searchTerm !== value.HeatName) {
        setSelectedHeat({
          Name: value.HeatName,
          Id: value.HeatId!,
        });
      }
    }
  }, [isFocused, searchTerm, value, loading, isProcessLoading]);

  return (
    <>
      <Grid
        container
        direction={"row"}
        justifyContent={"space-around"}
        alignItems={"center"}
        spacing={1}
        sx={{
          display: "grid",
          gridTemplateColumns: "15% 55% 15% 15%",
        }}
      >
        <Grid item>
          <IconButton
            size={"small"}
            aria-label="previous"
            color={"primary"}
            disabled={!showPrevious}
            onClick={() => {
              onPrevious?.(value.PreviousHeatId);
            }}
          >
            <ArrowBackRounded style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
        <Grid item style={{ flexGrow: 1 }}>
          <Autocomplete<HeatProps>
            autoHighlight
            blurOnSelect
            clearOnEscape
            selectOnFocus
            popupIcon={null}
            options={options}
            getOptionLabel={(option) => option?.Name || "Unlinked"}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            value={selectedHeat}
            onChange={handleSelectionChange}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
            onFocus={() => {
              setFocused(true);
              onFocus && onFocus();
            }}
            onBlur={() => {
              setFocused(false);
              onBlur && onBlur();
            }}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Heat Name"
                variant="outlined"
                size="small"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    style: {
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "center",
                      paddingRight: "8px",
                    },
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <InputAdornment position="end">
                          <IconButton
                            size={"small"}
                            title={value.HeatId ? "Unlink" : "Link"}
                            onClick={onButtonClick}
                            style={{
                              color: value.HeatId
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                            }}
                          >
                            {loading ? (
                              <CircularProgress size="1rem" />
                            ) : (
                              iconButton
                            )}
                          </IconButton>
                        </InputAdornment>
                      </>
                    ),
                  },
                  htmlInput: {
                    ...params.inputProps,
                    style: {
                      textAlign: "center",
                    },
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item>
          <IconButton
            aria-label="next"
            color={"primary"}
            disabled={!showNext}
            onClick={() => {
              onNext?.(value.NextHeatId);
            }}
            size="small"
          >
            <ArrowForwardRounded style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton
            color={"primary"}
            onClick={onLast}
            disabled={!showNext}
            title="Last process"
            size="small"
          >
            <SkipNext style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
